import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChecklistDocumentViewModel, ChecklistItemDocumentViewModel } from '../../../../shared/models/checklists/checklistAttachmentsViewModel';
import { DocumentViewModel } from '../../../../shared/viewModels/documents/documentViewModel';
import { ChecklistItemViewModel } from '../../../../shared/models/checklists/checklistItemViewModel';
import { ObjectTypes } from '../../../../shared/enums/objectTypes';
import { T } from 'src/assets/i18n/translation-keys';
import { TranslateService } from '@ngx-translate/core';
import { ChecklistModalComponent } from 'src/app/modules/shared/components/common/checklists/modals/checklist-modal/checklist-modal.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { DocumentAbstraction } from '../../../services/abstractions/document.abstraction';
import { ConfirmationService } from '../../../services/confirmation.service';
import { CommentEventsEmitter } from '../../../events/comments.events';
import { AttachmentsEventsEmitter } from '../../../events/attachments.events';
import { AlertService } from '../../../services/alert.service';
import { ExternalUserDocumentService } from '../../../services/abstractions/external-user-document.service';
import { NormalUserDocumentService } from '../../../services/abstractions/normal-user-document.service';
import { Subscription, take } from 'rxjs';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';
import { ObjectEventEmitters } from '../../../events/object.events';

@Component({
    selector: 'app-checklist-attachments',
    templateUrl: './checklist-attachments.component.html',
    styleUrls: ['./checklist-attachments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChecklistAttachmentsComponent implements OnInit, OnChanges {
  @Input() checklistAttachments: ChecklistDocumentViewModel[] = [];
  @Input() accountId: number;
  @Input() objectType: ObjectTypes;
  @Input() showViewChecklistButton: boolean = true;
  @Output() attachmentsChanged = new EventEmitter<void>();
  @Output() checklistItemAttachmentsCountChanged = new EventEmitter<ChecklistItemViewModel>();
  private activeDocumentService: DocumentAbstraction;
  public readonly T = T;
  public hasAnyAttachments: boolean = false;

  private _loading: boolean = false;
  public get loading(): boolean {
    return this._loading;
  }
  public set loading(value: boolean) {
    this._loading = value;
     this.changeDetectorRef.markForCheck();
  }
  private subscriptions = new Subscription();
  private isExternalUser: boolean = false;
  private externalShareId: string | null = null;
  constructor(
    protected readonly translateService: TranslateService,
    private readonly modalService: BsModalService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly confirmationService: ConfirmationService,
    private readonly commentsEventEmitter: CommentEventsEmitter,
    protected readonly attachmentsEventsEmitter: AttachmentsEventsEmitter,
    private readonly alertService: AlertService,
    private readonly normalUserDocumentService: NormalUserDocumentService,
    private readonly externalUserDocumentService: ExternalUserDocumentService,
    private readonly externalAuthService: ExternalAuthService,
    private readonly objectEventEmitters: ObjectEventEmitters,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.externalAuthService.currentSession$.pipe(take(1)).subscribe((session) => {
        if (session !== null && this.externalAuthService.isAuthenticated()) {
          this.isExternalUser = true;
          this.externalShareId = session.externalShareId;
          this.activeDocumentService = this.externalUserDocumentService;
        } else {
          this.isExternalUser = false;
          this.externalShareId = null;
          this.activeDocumentService = this.normalUserDocumentService;
        }
      })
    );

    this.subscriptions.add(
      this.attachmentsEventsEmitter.attachmentDeletedSuccessfully$.subscribe((deletedDocument) => {
        const affectedChecklistItem = this.removeAttachmentFromLocalArray(deletedDocument.id);
        this.updateHasAnyAttachments();

        if (affectedChecklistItem) {
          this.checklistItemAttachmentsCountChanged.emit(affectedChecklistItem);
          this.objectEventEmitters.broadcastObjectUpdated(
            affectedChecklistItem.id,
            ObjectTypes.Checklist_Item,
            affectedChecklistItem
          );
        }

        this.attachmentsChanged.emit();
        this.changeDetectorRef.markForCheck();
      })
    );

    this.updateHasAnyAttachments();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['checklistAttachments']) {
      this.updateHasAnyAttachments();
    }
  }

  private updateHasAnyAttachments(): void {
    this.hasAnyAttachments = this.checklistAttachments && this.checklistAttachments.some(checklist =>
      this.hasChecklistAttachments(checklist.checklistId)
    );
    this.changeDetectorRef.markForCheck();
  }

  public getTotalChecklistAttachmentsCount(): number {
    let totalCount = 0;
    this.checklistAttachments.forEach(checklist => {
      totalCount += this.getChecklistAttachmentsCount(checklist.checklistId);
    });
    return totalCount;
  }

  public getChecklistAttachmentsCount(checklistTemplateId: number): number {
    const group = this.getChecklistAttachmentsByTemplateId(checklistTemplateId);
    if (!group || !group.checklistItemWithDocuments) {
      return 0;
    }

    let count = 0;
    group.checklistItemWithDocuments.forEach(item => {
      if (item.documents && item.documents.length > 0) {
        count += item.documents.length;
      }
    });

    return count;
  }

  public hasChecklistAttachments(checklistTemplateId: number): boolean {
    const group = this.getChecklistAttachmentsByTemplateId(checklistTemplateId);

    if (!group) {
      return false;
    }

    if (!group.checklistItemWithDocuments) {
      return false;
    }

    if (group.checklistItemWithDocuments.length === 0) {
      return false;
    }

    const hasDocuments = group.checklistItemWithDocuments.some(item => {
      return item.documents && item.documents.length > 0;
    });

    return hasDocuments;
  }


  public getChecklistAttachmentsByTemplateId(checklistTemplateId: number): ChecklistDocumentViewModel | undefined {
    return this.checklistAttachments.find(group => group.checklistId === checklistTemplateId);
  }

  public getAllChecklistDocuments(checklistTemplateId: number): DocumentViewModel[] {
    const group = this.getChecklistAttachmentsByTemplateId(checklistTemplateId);
    if (!group || !group.checklistItemWithDocuments) {
      return [];
    }

    const allDocuments: DocumentViewModel[] = [];
    group.checklistItemWithDocuments.forEach(item => {
      if (item.documents && item.documents.length > 0) {
        const contextualInfo = this.getChecklistItemContextualInfo(item);

        item.documents.forEach(doc => {
          (doc as any).contextualInfo = contextualInfo;
        });

        allDocuments.push(...item.documents);
      }
    });

    return allDocuments;
  }

  public getDocumentContextualInfoMapping(checklistTemplateId: number): Map<number, any> {
    const group = this.getChecklistAttachmentsByTemplateId(checklistTemplateId);
    const mapping = new Map<number, any>();

    if (group && group.checklistItemWithDocuments) {
      group.checklistItemWithDocuments.forEach(item => {
        if (item.documents && item.documents.length > 0) {
          const contextualInfo = this.getChecklistItemContextualInfo(item);
          item.documents.forEach(doc => {
            mapping.set(doc.id, contextualInfo);
          });
        }
      });
    }

    return mapping;
  }

  public getChecklistItemContextualInfo(checklistItemDocuments: ChecklistItemDocumentViewModel): any {
    const checklistItem: ChecklistItemViewModel = checklistItemDocuments.checklistItem;

    return {
      objectType: ObjectTypes.Checklist_Item,
      title: checklistItem.title,
      responseType: checklistItem.responseType,
      response: checklistItem.response,
      textResponse: checklistItem.textResponse
    };
  }

  public onViewChecklist(checklist: ChecklistDocumentViewModel): void {
    const initialState = {
      checklistItems: checklist?.checklistItemWithDocuments.map(item => item.checklistItem),
      canEdit: true,
      title: checklist.checklistTitle ? checklist.checklistTitle : this.translateService.instant(T.defaultLocalizations.checklist_item.additional)
    };

    const modalConfig = { ignoreBackdropClick: true, class: 'modal-right-end-full-height-add', };
    const modalParams = Object.assign({}, modalConfig, { initialState });
    const modalRef: BsModalRef = this.modalService.show(ChecklistModalComponent, modalParams);

    if (modalRef.content) {
      modalRef.content.checklistAttachmentsChanged.subscribe(() => {
        this.loading = true;
        this.attachmentsChanged.emit();
      });
    }
  }

  public onAttachmentsRefreshComplete(): void {
    this.hasAnyAttachments = this.checklistAttachments.some(checklist =>
      this.hasChecklistAttachments(checklist.checklistId)
    );
    this.loading = false;
  }

  public attachmentDownload(document: DocumentViewModel): void {
    this.activeDocumentService.downloadDocument(document.id);
  }

  public attachmentLinkClicked(object: DocumentViewModel): void {
    window.open(object.filename, '_blank');
  }


  public deleteDocument(document: DocumentViewModel): void {
    this.confirmationService.confirmThis(
      this.translateService.instant(T.common.confirm_delete_item, {
        item: this.translateService.instant(T.common.attachment.one),
      }),
      () => {
        this.activeDocumentService.deleteDocument(document.id, document.globalObjectId, document.globalObjectType).subscribe(() => {
          const affectedChecklistItem = this.removeAttachmentFromLocalArray(document.id);
          this.updateHasAnyAttachments();

          if (affectedChecklistItem) {
            this.checklistItemAttachmentsCountChanged.emit(affectedChecklistItem);
            this.objectEventEmitters.broadcastObjectUpdated(
              affectedChecklistItem.id,
              ObjectTypes.Checklist_Item,
              affectedChecklistItem
            );
          }

          this.attachmentsChanged.emit();
          this.commentsEventEmitter.broadcastCommentAdded(document.globalObjectId);
          this.changeDetectorRef.markForCheck();
          this.attachmentsEventsEmitter.broadcastAttachmentDeletedSuccessfully(document);
        });
        void this.alertService.success(
          this.translateService.instant(T.common.item_deleted, { item: this.translateService.instant(T.common.document.one) })
        );
      },
      () => {},
      undefined,
      true
    );
  }

  private removeAttachmentFromLocalArray(documentId: number): ChecklistItemViewModel | null {
    let affectedChecklistItem: ChecklistItemViewModel | null = null;

    this.checklistAttachments.forEach(checklist => {
      if (checklist.checklistItemWithDocuments) {
        checklist.checklistItemWithDocuments.forEach(item => {
          if (item.documents) {
            const index = item.documents.findIndex(doc => doc.id === documentId);
            if (index !== -1) {
              item.documents.splice(index, 1);
              if (item.checklistItem) {
                item.checklistItem.attachmentsCount = item.documents.length;
                affectedChecklistItem = item.checklistItem;
              }
            }
          }
        });
      }
    });

    return affectedChecklistItem;
  }
}
